import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { MAX_STEPS, MAX_STEP_LENGTH, moveStep, newStep, type StepDraft } from '@/lib/recipeCard/methodDraft'
import { RC } from '@/lib/recipeCard/recipeCardStrings'

/**
 * The numbered method of a recipe, one step per box. Controlled: the parent
 * owns the draft (a dish's method card, or the prep item drawer) and decides
 * when to save. Used by both, so a dish and a prep item are written the same way.
 */
export function RecipeStepsEditor({ steps, onChange, idPrefix }: {
  steps: StepDraft[]
  onChange: (steps: StepDraft[]) => void
  idPrefix: string
}) {
  const setText = (key: string, text: string) => onChange(steps.map((s) => (s.key === key ? { ...s, text } : s)))
  return (
    <div className="space-y-2">
      <div>
        <h5 className="text-sm font-semibold">{RC.stepsTitle}</h5>
        <p className="text-xs text-muted-foreground">{RC.stepsHelp}</p>
      </div>
      {steps.length > 0 && (
        <ol className="space-y-2">
          {steps.map((s, i) => (
            <li key={s.key} className="flex items-start gap-2">
              <label htmlFor={`${idPrefix}-step-${s.key}`} className="mt-2 w-6 shrink-0 text-right text-sm font-semibold tabular-nums text-muted-foreground">
                <span className="sr-only">{RC.stepLabel(i + 1)}</span>
                <span aria-hidden>{i + 1}.</span>
              </label>
              <Textarea
                id={`${idPrefix}-step-${s.key}`}
                rows={2}
                maxLength={MAX_STEP_LENGTH}
                placeholder={RC.stepPlaceholder}
                className="flex-1"
                value={s.text}
                onChange={(e) => setText(s.key, e.target.value)}
              />
              {/* h-9 w-9: the icon-button size the Costing & Recipes sheet already uses. */}
              <Button type="button" variant="ghost" size="icon" className="h-9 w-9 shrink-0 text-muted-foreground" disabled={i === 0}
                aria-label={RC.moveUp(i + 1)} onClick={() => onChange(moveStep(steps, s.key, -1))}>
                <ArrowUp className="h-4 w-4" />
              </Button>
              <Button type="button" variant="ghost" size="icon" className="h-9 w-9 shrink-0 text-muted-foreground" disabled={i === steps.length - 1}
                aria-label={RC.moveDown(i + 1)} onClick={() => onChange(moveStep(steps, s.key, 1))}>
                <ArrowDown className="h-4 w-4" />
              </Button>
              <Button type="button" variant="ghost" size="icon" className="h-9 w-9 shrink-0 text-muted-foreground hover:text-destructive"
                aria-label={RC.removeStep(i + 1)} onClick={() => onChange(steps.filter((x) => x.key !== s.key))}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </li>
          ))}
        </ol>
      )}
      <Button type="button" variant="outline" size="sm" className="gap-1" disabled={steps.length >= MAX_STEPS}
        onClick={() => onChange([...steps, newStep()])}>
        <Plus className="h-3.5 w-3.5" /> {RC.addStep}
      </Button>
    </div>
  )
}
